import React, { useState, useRef } from 'react';
import { User, Check, X } from 'lucide-react';
import { useNotesUIStore } from '../../../../store/notesUIStore';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';
import { useOutsideClick } from '../../../../hooks/useOutsideClick';
import { FilterButton } from '../../../ui/FilterButton';
import { formatUserDisplayName } from '../../../../lib/utils';
import { useViewport } from '../../../../contexts/ViewportContext';
import { getCachedUser } from '../../../../utils/userCache';

/**
 * NoteUserFilterDropdown component
 *
 * Displays a dropdown for filtering notes by author/user.
 * Styled to match TaskAssigneeFilterDropdown.
 */
interface NoteUserFilterDropdownProps {
  filters: UserFilter[];
  onToggleFilter: (userId: string, isExclusive?: boolean) => void;
  isMobile?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
}

export function NoteUserFilterDropdown({
  filters,
  onToggleFilter,
  isMobile: propIsMobile,
  isOpen: propIsOpen,
  onToggle: propOnToggle,
}: NoteUserFilterDropdownProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { activeFilters } = useNotesUIStore();
  const { isMobile: viewportIsMobile } = useViewport();

  // Use passed in props or fallback to internal state
  const isMobile = propIsMobile !== undefined ? propIsMobile : viewportIsMobile;
  const isOpen = propIsOpen !== undefined ? propIsOpen : internalIsOpen;
  const setIsOpen = propOnToggle
    ? (newValue: boolean) => {
        if (newValue !== isOpen) propOnToggle();
      }
    : setInternalIsOpen;

  // Count how many filters are active
  const activeCount = filters.filter(f => activeFilters.userIds.includes(f.id)).length;

  // Handle outside clicks to close the dropdown
  useOutsideClick(dropdownRef, () => {
    setIsOpen(false);
  });

  // Handle toggling the dropdown
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Handle clearing all filters
  const handleClear = () => {
    // Clear all active user filters by calling toggle on each active one
    filters.forEach(filter => {
      if (activeFilters.userIds.includes(filter.id)) {
        onToggleFilter(filter.id, false);
      }
    });
  };

  // Get button text based on selected users
  const getButtonText = () => {
    const activeUsers = filters.filter(filter => activeFilters.userIds.includes(filter.id));

    if (activeUsers.length === 0) {
      return 'Authors';
    } else if (activeUsers.length === 1) {
      return formatUserDisplayName(activeUsers[0].name);
    } else {
      return `Authors (${activeUsers.length})`;
    }
  };

  // Fix for the infinite loop - make sure we handle the filter correctly
  const handleUserFilter = (userId: string) => {
    // Prevent re-toggling a user filter that causes an infinite loop
    onToggleFilter(userId, true);
    // Close the dropdown after selection to prevent loops
    setIsOpen(false);
  };

  // Use FilterButton component for consistent styling
  const userFilterButton = (
    <FilterButton
      isActive={isOpen}
      hasFilters={activeCount > 0}
      onClick={handleToggle}
      onClear={e => {
        e.stopPropagation();
        handleClear();
      }}
      label={getButtonText()}
      icon={<User size={16} className="text-blue-500" />}
      isMobile={isMobile}
      className=""
    />
  );

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {userFilterButton}

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-10 right-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200">
          <div className="py-1">
            {/* Header with clear button */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-700">Filter by author</span>
              {activeCount > 0 && (
                <button
                  onClick={handleClear}
                  className="text-blue-400 hover:text-blue-600 transition-colors"
                  aria-label="Clear author selection"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* User list */}
            <div className="max-h-60 overflow-y-auto">
              {filters.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500 italic">No authors available</div>
              ) : (
                <>
                  {/* List of filters */}
                  {filters.map(filter => {
                    // Use cached user data if available, or fall back to filter data
                    const cachedUser = getCachedUser(filter.id);
                    const avatarUrl = cachedUser?.avatar_url || filter.avatar_url;
                    const displayName = formatUserDisplayName(filter.name || '');

                    return (
                      <button
                        key={filter.id}
                        onClick={() => handleUserFilter(filter.id)}
                        className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-50"
                      >
                        <div className="flex-1 flex items-center gap-2">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={displayName}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                              {displayName?.charAt(0) || '?'}
                            </div>
                          )}
                          <span className="truncate">{displayName}</span>
                        </div>
                        <div
                          className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                            activeFilters.userIds.includes(filter.id)
                              ? 'bg-green-500 border-green-500'
                              : 'border-gray-300'
                          }`}
                        >
                          {activeFilters.userIds.includes(filter.id) && (
                            <Check size={14} className="text-white" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
